import { get } from "@ember/object"
import type { FindFn } from "@shared/models/analytics/base"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../../action-doc-builder"
import _, { partial } from "lodash"
import moment from "moment"
import { findPlanbookDatesOff } from "../../../dates-off-calculator"
import type { Narrative } from "../../create-narrative"
import defaultPlanbookDateActivity from "./default-planbook-date-activity"
import defaultPlanbookDateContext from "./default-planbook-date-context"
import defaultPlanbookDateModels from "./default-planbook-date-models"

interface Payload {
  annotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
  planbook: Planbook
  bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING"
  bumpDeleteOrNothingPatches: Array<PatchModel>
  bumpDeleteOrNothingUndoPatches: Array<PatchModel>
}

export default function preparePlanbookDateAnnotationUpdate(payload: Payload): PreparedAction {
  return {
    name: "PLANBOOK_DATE_ANNOTATION_UPDATE",
    payload: payload,
    patches: payload.bumpDeleteOrNothingPatches.concat(patches(payload)),
    undoPatches: payload.bumpDeleteOrNothingUndoPatches.concat(undoPatches(payload)),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let newAnnotations = _.map(payload.planbook.attributes.calendar.dateAnnotations, (annotation) => {
    return annotation.id === payload.annotation.id ? payload.annotation : annotation
  })
  let allDatesOff = findPlanbookDatesOff(newAnnotations)

  let patch = Patch.create("planbook", payload.planbook.id, "attributes.calendar.dateAnnotations", {
    id: payload.annotation.id,
  })
    .set("embedded", `attributes`, payload.annotation.attributes)
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let oldAnnotations = JSON.parse(
    JSON.stringify(payload.planbook.attributes.calendar.dateAnnotations)
  )
  let allDatesOff = findPlanbookDatesOff(oldAnnotations)
  let oldAnnotation = _.find(oldAnnotations, (annotation) => {
    return annotation.id === payload.annotation.id
  })

  let patch = Patch.create("planbook", payload.planbook.id, "attributes.calendar.dateAnnotations", {
    id: payload.annotation.id,
  })
    .set("embedded", `attributes`, oldAnnotation.attributes)
    .set("attributes.calendar.datesOff", allDatesOff)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultPlanbookDateModels(payload, findFn, userId)
  let context = defaultPlanbookDateContext(payload, models)
  // @ts-ignore
  let weekdayTitle = get(models, "rotation.attributes.title")
  let oldAnnotation = _.find(payload.planbook.attributes.calendar.dateAnnotations, (annotation) => {
    return annotation.id === payload.annotation.id
  })!

  let dateStringOld =
    oldAnnotation.type === "date-annotation-planbook-custom-single"
      ? oldAnnotation.attributes.dateString
      : null
  let startDateOld =
    oldAnnotation.type === "date-annotation-planbook-custom-range"
      ? oldAnnotation.attributes.startDate
      : null
  let endDateOld =
    oldAnnotation.type === "date-annotation-planbook-custom-range"
      ? oldAnnotation.attributes.endDate
      : null
  let titleOld = oldAnnotation.attributes["title"]
  let descriptionOld = oldAnnotation.attributes["description"]
  let isOffOld = oldAnnotation.attributes["isOff"]
  let newContext = _.assign({}, context, {
    annotationId: payload.annotation.id,
    dateStringOld,
    startDateOld,
    endDateOld,
    titleOld,
    isOffOld,
    bumpDeleteOrNothing: payload.bumpDeleteOrNothing,
  })

  let formattedDateString = moment(get(context, "dateString")).format("ddd, MMM. D, YYYY")
  let formattedStartDate = moment(get(context, "startDate")).format("ddd, MMM. D, YYYY")
  let formattedEndDate = moment(get(context, "endDate")).format("ddd, MMM. D, YYYY")
  let formattedDateStringOld = moment(dateStringOld).format("ddd, MMM. D, YYYY")
  let formattedStartDateOld = moment(startDateOld).format("ddd, MMM. D, YYYY")
  let formattedEndDateOld = moment(endDateOld).format("ddd, MMM. D, YYYY")

  var noteUpdates = (function () {
    let hasNewTitle = titleOld !== payload.annotation.attributes.title
    let hasNewDescription = descriptionOld !== payload.annotation.attributes.description
    switch (context.type) {
      case "custom-single":
        return hasNewTitle
          ? hasNewDescription
            ? `Updated title and description of note on ${formattedDateStringOld}.`
            : `Updated title of note on ${formattedDateStringOld}.`
          : hasNewDescription
          ? `Updated description of note on ${formattedDateStringOld}.`
          : ""
      case "custom-range":
        return hasNewTitle
          ? hasNewDescription
            ? `Updated title and description of note running from ${formattedStartDateOld} to ${formattedEndDateOld}.`
            : `Updated title of note running from ${formattedStartDateOld} to ${formattedEndDateOld}.`
          : hasNewDescription
          ? `Updated description of note running from ${formattedStartDateOld} to ${formattedEndDateOld}.`
          : ""
      case "default":
        return hasNewTitle
          ? hasNewDescription
            ? `Updated title and description of note.`
            : `Updated title of note.`
          : hasNewDescription
          ? `Updated description of note.`
          : ""
    }
  })()

  var dateUpdates = (function () {
    switch (context.type) {
      case "custom-single":
        return dateStringOld !== get(context, "dateString")
          ? `Moved note from ${formattedDateStringOld} to ${formattedDateString}`
          : ""
      case "custom-range":
        return startDateOld !== get(context, "startDate") || endDateOld !== get(context, "endDate")
          ? `Set note to show on every day from ${formattedStartDate} to ${formattedEndDate}.`
          : ""
      case "default":
        return `Set note to appear every ${weekdayTitle}.`
    }
  })()

  var isOffUpdates = (function () {
    if (isOffOld === payload.annotation.attributes.isOff) return ""
    let planbookDatesOff = get(models, "planbook.attributes.calendar.datesOff") as any
    let turnedOff = _.includes(planbookDatesOff, findPlanbookDatesOff([payload.annotation]))
    switch (context.type) {
      case "custom-single":
        return turnedOff
          ? `Turned off all classes on ${formattedDateString}.`
          : `Turned all classes on ${formattedDateString} back on.`
      case "custom-range":
        return turnedOff
          ? `Turned off all classes on every day from ${formattedStartDate} to ${formattedEndDate}.`
          : `Turned all classes on every day from ${formattedStartDate} to ${formattedEndDate} back on.`
      case "default":
        return turnedOff
          ? `Turned off all classes on every ${weekdayTitle}.`
          : `Turned all classes on every ${weekdayTitle} back on.`
    }
  })()

  let analyticsModifier =
    payload.annotation.type === "date-annotation-planbook-custom-single"
      ? "Single"
      : payload.annotation.type === "date-annotation-planbook-custom-range"
      ? "Range"
      : "Default"

  return {
    title: "Edited A Date",
    titleForAnalytics: `Planbook Date - Update Annotation - ${analyticsModifier}`,
    description: `${noteUpdates} ${dateUpdates} ${isOffUpdates}`,
    context: newContext,
    activity: defaultPlanbookDateActivity(payload, models),
  }
}
